import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { Link } from 'wouter';

import { useLayout } from '../../contexts';

import MenuIcons, { MenuIconKey } from '../icons/menuIcons';
import Menu from '../menus/Menu';
import Logo from './Logo';

import styles from '../../../styles/partials/main-menu.module.css';

interface MenuItem {
    label: string;
    url: string;
    key?: string;
    current?: boolean;
}

interface MainMenuProps {
    logoLink: string;
    menuItems: MenuItem[];
    pagesMenuItems: MenuItem[];
    onClickItem?: () => void;
    menuOpened: boolean;
}

function MainMenu({ logoLink, menuItems, pagesMenuItems, menuOpened }: MainMenuProps) {
    // Add icons to menu items based on their key
    const menuItemsWithIcons = useMemo(() => {
        return menuItems.map((item) => {
            const { key, label, ...rest } = item;
            const iconKey = key as MenuIconKey;
            const IconComponent = key && MenuIcons[iconKey] ? MenuIcons[iconKey] : null;

            return {
                ...rest,
                label: (
                    <span className={styles.inner}>
                        <span className={styles.middle}>
                            {IconComponent && <IconComponent className={styles.icon} />}
                            <span className={styles.label}>
                                <span>{label}</span>
                            </span>
                        </span>
                    </span>
                ),
                current: item.current,
            };
        });
    }, [menuItems]);

    const { setMenuOpened } = useLayout();
    const onClickItem = useCallback(() => {
        if (menuOpened) {
            window.scrollTo({
                behavior: 'smooth',
                top: 0,
            });
            setMenuOpened(false);
        }
    }, [menuOpened, setMenuOpened]);

    return (
        <nav
            className={classNames({
                [styles.container]: true,
                [styles.menuOpened]: menuOpened,
            })}
            aria-label="Main navigation"
        >
            <div className={styles.cols}>
                <div className={classNames([styles.col, styles.colLogo])}>
                    <Link href={logoLink} className={styles.logoContainer} aria-label="Home">
                        <Logo className={styles.logo} />
                    </Link>
                </div>
                <div className={classNames([styles.col, styles.colMenu])}>
                    <div className={styles.menuContainer}>
                        <div className={styles.menu}>
                            <ul className={styles.items}>
                                {menuItemsWithIcons.map((item, index) => {
                                    const {
                                        label,
                                        url,
                                        current = false,
                                        external = false,
                                        ...rest
                                    } = item;
                                    const key = (rest as any).id || (rest as any).key || index;
                                    return (
                                        <li
                                            key={key}
                                            className={classNames({
                                                [styles.item]: true,
                                                [styles.current]: current,
                                            })}
                                        >
                                            {external ? (
                                                <a
                                                    href={url}
                                                    className={styles.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {label}
                                                </a>
                                            ) : (
                                                <Link
                                                    href={url}
                                                    onClick={onClickItem}
                                                    className={styles.link}
                                                >
                                                    {label}
                                                </Link>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        {pagesMenuItems.length > 0 && (
                            <div className={styles.pagesMenu}>
                                <Menu
                                    items={pagesMenuItems}
                                    itemsClassName={styles.items}
                                    itemClassName={styles.item}
                                    linkClassName={styles.link}
                                    currentClassName={styles.current}
                                    onClickItem={onClickItem}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
}

export default MainMenu;
